import { StandardEvents } from '@shopify/events';

const CONFIRMED_CART_EVENT = 'cgau:cart-confirmed';
const SHIPPING_CLASS = 'cgau-shipping-threshold-updated';
const LINE_CLASS = 'cgau-cart-line-confirmed';
const reduceMotion = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)');
const timers = new Set();
const renderObservers = new Set();
let addSequenceCounter = 0;
let latestConfirmedSequence = 0;

const motionObserver = typeof IntersectionObserver === 'function'
  ? new IntersectionObserver((entries) => entries.forEach((entry) => {
    if (entry.isIntersecting) return;
    entry.target.classList.remove(SHIPPING_CLASS, LINE_CLASS);
    if (entry.target.classList.contains('cgau-cart-confirmation')) entry.target.dataset.cgauMotion = 'off';
    motionObserver.unobserve(entry.target);
  }))
  : null;

const later = (callback, delay) => {
  const timer = globalThis.setTimeout(() => {
    timers.delete(timer);
    callback();
  }, delay);
  timers.add(timer);
  return timer;
};

const shippingKey = (element) => {
  const context = element.closest('#cart-drawer') ? 'drawer' : 'page';
  const section = element.closest('cart-items-component')?.dataset.sectionId
    || element.closest('.shopify-section')?.id
    || 'cart';
  return `${context}:${section}`;
};
const shippingSnapshot = () => new Map(
  [...document.querySelectorAll('.cgau-cart-help')].map((element) => [
    shippingKey(element),
    `${element.dataset.cgauCartCurrency || ''}:${element.dataset.cgauCartTotal || ''}:${element.dataset.cgauShipping || ''}`,
  ])
);

const visible = (element) => {
  if (!(element instanceof Element) || document.hidden) return false;
  const rect = element.getBoundingClientRect();
  return rect.bottom > 0 && rect.top < globalThis.innerHeight && rect.right > 0 && rect.left < globalThis.innerWidth;
};

const idPart = (value) => String(value || '').split('/').pop();

const addedItemFrom = (event, detail) => {
  const items = Array.isArray(detail?.items) ? detail.items : [];
  const merchandiseIds = new Set(
    (Array.isArray(event.lines) ? event.lines : [])
      .map((line) => idPart(line?.merchandiseId || line?.id))
      .filter(Boolean)
  );
  const productId = idPart(detail?.productId);
  const item = items.find((candidate) => merchandiseIds.has(idPart(candidate?.variant_id || candidate?.id)))
    || items.find((candidate) => productId && productId === idPart(candidate?.product_id))
    || null;
  return item ? {
    key: String(item.key || ''),
    title: String(item.product_title || item.title || '').trim().slice(0, 220),
    image: typeof item.image === 'string' ? item.image : '',
  } : null;
};

const animateShippingThreshold = (before) => {
  document.querySelectorAll('.cgau-cart-help').forEach((element) => {
    const key = shippingKey(element);
    const after = `${element.dataset.cgauCartCurrency || ''}:${element.dataset.cgauCartTotal || ''}:${element.dataset.cgauShipping || ''}`;
    if (before.get(key) === after || !visible(element)) return;
    element.classList.remove(SHIPPING_CLASS);
    void element.offsetWidth;
    element.classList.add(SHIPPING_CLASS);
    motionObserver?.observe(element);
    later(() => element.classList.remove(SHIPPING_CLASS), 560);
  });
};

const confirmationMarkup = (item) => {
  const notice = document.createElement('div');
  notice.className = 'cgau-cart-confirmation';
  notice.setAttribute('role', 'status');
  notice.setAttribute('aria-live', 'polite');
  notice.setAttribute('aria-atomic', 'true');
  const check = document.createElement('span');
  check.className = 'cgau-cart-confirmation__check';
  check.setAttribute('aria-hidden', 'true');
  check.textContent = '✓';
  const copy = document.createElement('span');
  copy.className = 'cgau-cart-confirmation__copy';
  const strong = document.createElement('strong');
  strong.textContent = 'Added to your cart';
  const name = document.createElement('span');
  name.textContent = item.title;
  copy.append(strong, name);
  if (item.image) {
    const image = document.createElement('img');
    image.src = item.image;
    image.alt = '';
    image.width = 54;
    image.height = 54;
    notice.append(image);
  }
  notice.append(check, copy);
  return notice;
};

const cartRenderState = (item, before) => {
  const drawer = document.getElementById('cart-drawer');
  const drawerOpen = Boolean(drawer?.hasAttribute('open') || drawer?.querySelector('dialog[open]'));
  const rows = drawer ? [...drawer.querySelectorAll('.cart-items__table-row[data-key]')] : [];
  const matchingRow = item.key ? rows.find((row) => row.dataset.key === item.key) : rows.find((row) => row.classList.contains('adding'));
  const shippingHelpers = [...document.querySelectorAll('.cgau-cart-help')];
  const shippingChanged = shippingHelpers.some((element) => {
    const after = `${element.dataset.cgauCartCurrency || ''}:${element.dataset.cgauCartTotal || ''}:${element.dataset.cgauShipping || ''}`;
    return before.get(shippingKey(element)) !== after;
  });
  const shippingSettled = shippingHelpers.length === 0 || shippingChanged;
  return { drawer, drawerOpen, matchingRow, shippingSettled };
};

const afterCartRender = (item, before, callback, timeout = 1600, requireDrawer = true, requireShipping = false) => {
  const observedRoot = document.body;
  let finished = false;
  let timeoutId;
  const observer = new MutationObserver(() => check(false));
  const finish = (state) => {
    if (finished) return;
    finished = true;
    observer.disconnect();
    renderObservers.delete(observer);
    if (timeoutId) {
      globalThis.clearTimeout(timeoutId);
      timers.delete(timeoutId);
    }
    callback(state);
  };
  const check = (timedOut) => {
    const state = cartRenderState(item, before);
    if (document.hidden) return finish(state);
    const rowReady = Boolean(state.matchingRow || !item.key);
    if (timedOut || ((!requireDrawer || state.drawerOpen) && rowReady && (!requireShipping || state.shippingSettled))) finish(state);
  };
  renderObservers.add(observer);
  observer.observe(observedRoot, { attributes: true, childList: true, subtree: true });
  timeoutId = later(() => check(true), timeout);
  globalThis.requestAnimationFrame(() => check(false));
};

const settleConfirmedCart = (item, before, sequence) => {
  afterCartRender(item, before, ({ drawer, drawerOpen, matchingRow }) => {
    if (sequence !== latestConfirmedSequence || !drawerOpen || document.hidden) return;
    const itemsRegion = drawer?.querySelector('.cart-drawer__items');
    if (!itemsRegion) return;
    drawer.querySelectorAll('.cgau-cart-confirmation').forEach((existing) => existing.remove());
    const notice = confirmationMarkup(item);
    itemsRegion.prepend(notice);
    if (visible(notice)) motionObserver?.observe(notice);
    else notice.dataset.cgauMotion = 'off';
    if (matchingRow && visible(matchingRow)) {
      matchingRow.classList.remove(LINE_CLASS);
      void matchingRow.offsetWidth;
      matchingRow.classList.add(LINE_CLASS);
      motionObserver?.observe(matchingRow);
      later(() => matchingRow.classList.remove(LINE_CLASS), 620);
    }
    later(() => notice.remove(), reduceMotion?.matches ? 2800 : 3200);
  });
};

const settleShipping = (before) => afterCartRender({ key: '' }, before, () => animateShippingThreshold(before), 1000, false, true);

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const before = shippingSnapshot();
  const addSequence = event.action === 'add' ? ++addSequenceCounter : 0;
  event.promise?.then(({ detail }) => {
    if (detail?.didError !== false) return;
    const item = event.action === 'add' ? addedItemFrom(event, detail) : null;
    const isNewestConfirmedAdd = event.action === 'add' && item?.title && addSequence > latestConfirmedSequence;
    if (isNewestConfirmedAdd) {
      latestConfirmedSequence = addSequence;
      document.dispatchEvent(new CustomEvent(CONFIRMED_CART_EVENT, {
        detail: { item, source: String(detail.source || 'cart-update') },
      }));
    }
    globalThis.requestAnimationFrame(() => {
      if (isNewestConfirmedAdd) settleConfirmedCart(item, before, addSequence);
      settleShipping(before);
    });
  }).catch(() => {});
});

document.addEventListener(CONFIRMED_CART_EVENT, (event) => {
  const drawer = document.getElementById('cart-drawer');
  if (drawer) drawer.dataset.cgauCartConfirmed = String(event.detail?.source || 'cart-update');
});

document.addEventListener('visibilitychange', () => {
  if (!document.hidden) return;
  renderObservers.forEach((observer) => observer.disconnect());
  renderObservers.clear();
  timers.forEach((timer) => globalThis.clearTimeout(timer));
  timers.clear();
  document.querySelectorAll(`.${SHIPPING_CLASS}, .${LINE_CLASS}`).forEach((element) => element.classList.remove(SHIPPING_CLASS, LINE_CLASS));
  document.querySelectorAll('.cgau-cart-confirmation').forEach((element) => element.remove());
});

reduceMotion?.addEventListener?.('change', () => {
  if (!reduceMotion.matches) return;
  document.querySelectorAll(`.${SHIPPING_CLASS}, .${LINE_CLASS}`).forEach((element) => {
    element.classList.remove(SHIPPING_CLASS, LINE_CLASS);
    motionObserver?.unobserve(element);
  });
  document.querySelectorAll('.cgau-cart-confirmation').forEach((element) => {
    element.dataset.cgauMotion = 'off';
    motionObserver?.unobserve(element);
  });
});
